import { Button, ControlGroup, ControlGroupProps, FormGroup, Intent, Icon } from "@blueprintjs/core";
import { get } from "lodash";
import React, { useCallback, useMemo } from "react";
import { evaluate } from "../..";
import { IList, IMetadata, IModel, IMutators } from "../../types";
import { doDisabled, doInvalid } from "../../utils";
import { css } from "@emotion/css";
import "./List.scss";

export interface IListProps extends ControlGroupProps {
  template: IList;
  model: IModel;
  mutators: IMutators;
  metadata: IMetadata;
  storedModel?: IModel;
}

const flexStyles = (template: IList) =>
  template.inline && template.layout
    ? css`
        ${template.layout.map(
      (v, i) => css`
            & > div:nth-child(${i + 1}) {
              flex: ${v} 1 auto;
            }
          `
    )}
      `
    : "";

const List = (props: IListProps) => {
  const { template, model, mutators, metadata } = props;
  const { getValue, handleCreate, handleRemove, isHidden } = mutators;

  const style = useMemo(() => flexStyles(template), [template]);

  const handleAddItem = useCallback(() => {
    handleCreate && handleCreate(template);
  }, [template, handleCreate]);

  const handleRemoveItem = useCallback(
    (i) => {
      handleRemove && handleRemove(template, i);
    },
    [template, handleRemove]
  );

  if ((isHidden && isHidden(template.name)) || (template.hidden && evaluate(model, template.hidden, getValue))) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  } else {
    if (metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput = metadata.hiddenInput.filter((i) => i === template.name);
    }
  }
  const count = get(model, [template.name, "length"], 0);
  const isAddable = handleCreate ? count < template.max : false;
  const isRemovable = handleRemove ? count > template.min : false;
  const outOfRange = count < template.min || count > template.max;
  const disabled = doDisabled(props);
  const invalid = doInvalid(props);

  return (
    <FormGroup
      key={template.name}
      intent={outOfRange || invalid ? Intent.DANGER : Intent.NONE}
      className={`${template.label ? "level-container" : ""} anchor-${template.name} list validate`}
      label={template.label}
      labelInfo={template.info}
      labelFor={template.name}
      helperText={!outOfRange && !invalid ? template.description : undefined}
    >
      {(outOfRange || invalid) && (
        <p className="fault">
          <Icon icon="error" intent="danger" />
          <span>
            {outOfRange
              ? `List must have ${template.min} to ${template.max} items.`
              : template.validate?.message}
          </span>
        </p>
      )}
      <Button
        onClick={handleAddItem}
        className="list__add-button"
        disabled={!isAddable || disabled}
        outlined={true}
        icon="add"
        intent="primary"
      >
        Add {template.item_label}
      </Button>
      <ControlGroup
        key={template.name}
        id={template.name}
        vertical={!template.inline}
        className="container"
      >
        {(props.children as Array<React.ReactNode>)?.map((child, i) => (
          <div
            key={`item-${get(model, [template.name, i, "id"])}`}
            className={`${template.item_label ? "level-container" : ""} list-item`}
          >
            <div className="list-item-header">
              <span>{`${template.item_label} ${i + 1}`}</span>
              <Button
                minimal
                intent="primary"
                onClick={() => handleRemoveItem(i)}
                disabled={!isRemovable || disabled}
                icon="remove"
              >
                REMOVE
              </Button>
            </div>
            <div className={`${template.inline ? "row" : "col"} ${style}`}>
              {child}
            </div>
          </div>
        ))}
      </ControlGroup>
    </FormGroup>
  );
};

export default List;
